import { markFieldToDelete } from "api/repositories/FirebaseRepository";
import axios from "axios";
import { Transaction, Unsubscribe } from "firebase/firestore";
import { enumHasValue } from "utils";
import { lang } from "../../i18nLang";
import { EContractStatus, ETechnicalRetentionType } from "../enums";
import {
  ContractHasMeasurementException,
  ContractSharedException,
} from "../exceptions/ContractException";
import {
  InvalidParamException,
  RequiredParamException,
} from "../exceptions/InvalidParamException";
import { BetweenValuesException } from "../exceptions/InvalidValueException";
import {
  IContractTaxes,
  IExecutiveBudget,
  IIndicatorQuotation,
  ITechnicalRetention,
} from "../interfaces";
import ContractModel from "../models/ContractModel";
import ContractRepository from "../repositories/ContractRepository";
import AttachmentService from "./AttachmentService";
import AuthService from "./AuthService";
import FileService from "./FileService";
import MeasurementService from "./MeasurementService";

/**
 * Service de contratos
 * @category Contracts
 * @hideconstructor
 */

class ContractService {
  /** Cria o contrato no banco de dados marcando o usuário logado como proprietário. */
  public async createContract(contract: ContractModel): Promise<void> {
    if (!contract.uid) return;

    this.validateContractData(contract);
    contract.addUserId(AuthService.currentUserId, true);

    return ContractRepository.addContractToDataBase(contract.uid, contract);
  }

  /** Atualiza os dados base do contrato no banco de dados. E adiciona log quando o
   * status é alterado.
   */
  public async updateContract(
    contract: ContractModel,
    updatedData: Partial<ContractModel>
  ): Promise<void> {
    const oldStatus = contract.status;
    const newStatus = updatedData.status!;

    this.validateContractData({ ...updatedData, date: contract.date });

    // Campos para serem removidos caso estejam vazios
    [
      "projectErp",
      "costCenter",
      "paymentCondition",
      "documentType",
      "financialIndicator",
    ].forEach((field) => {
      if (!updatedData[field]) updatedData[field] = markFieldToDelete();
    });

    await ContractRepository.updateContractOnDataBase(
      contract.uid,
      updatedData
    );

    if (newStatus !== oldStatus) {
      await this.addStatusLog(contract.uid, newStatus, oldStatus);
    }
  }

  private validateContractData(contract: Partial<ContractModel>) {
    const development = contract.development;

    if (!contract.name) throw new RequiredParamException("name");
    if (!contract.managementType)
      throw new RequiredParamException("managementType");
    if (!contract.status) throw new RequiredParamException("status");
    if (!enumHasValue(EContractStatus, contract.status))
      throw new InvalidParamException("status");
    if (!development || !development?.name)
      throw new RequiredParamException("project");
    if (!development.customer) throw new RequiredParamException("customer");
    if (!development.local) throw new RequiredParamException("location");

    const date = contract.date;
    const now = new Date();

    if (!date) throw new RequiredParamException("date");
    if (date.getTime() > now.getTime())
      throw new InvalidParamException(
        "date",
        lang("exceptions.contract.invalidDate")
      );

    const hasIntregrationMega =
      development.customerId ||
      contract.branchId ||
      contract.paymentCondition?.id ||
      contract.documentType?.id ||
      contract.financialIndicator?.id;

    if (hasIntregrationMega) {
      if (!development.customerId) throw new RequiredParamException("customer");
      if (!contract.branchId) throw new RequiredParamException("branch");
    }
  }

  /** Deleta o contrato do banco de dados, somente se este
   * não tiver medições criadas.
   * Obs: Só o proprietário pode excluir o contrato.
   */
  public async deleteContract(contract: ContractModel): Promise<void> {
    if (contract.ownerUserId !== AuthService.currentUserId) {
      throw new ContractSharedException();
    }

    if (await MeasurementService.countMeasurements(contract.uid)) {
      throw new ContractHasMeasurementException();
    }

    if (contract.coverImageUrl) {
      await this.removeContractCoverFromStorage(contract.coverImageUrl);
    }

    await AttachmentService.removeAllAttachmentsFromStorage(contract.uid);
    return ContractRepository.deleteContractFromDataBase(contract.uid);
  }

  private async removeContractCoverFromStorage(imageUrl: string) {
    const path = FileService.getPathFromUrl(imageUrl);
    await FileService.deleteFile(path);
  }

  /** Cria o log do Status no banco de dados. */
  private addStatusLog(
    uid: string,
    newStatus: string,
    oldStatus: string
  ): Promise<boolean> {
    const userName = AuthService.currentUser
      ? AuthService.currentUser.name || ""
      : "";

    return ContractRepository.addStatusChangedLog(uid, {
      userName,
      oldStatus,
      newStatus,
    });
  }

  /** Atualiza os dados da retenção técnica do contrato no banco de dados. */
  public async updateTechnicalRetention(
    uid: string,
    technicalRetention: ITechnicalRetention
  ): Promise<any> {
    this.validateTechnicalRetention(technicalRetention);
    if (!enumHasValue(ETechnicalRetentionType, technicalRetention.type))
      throw new InvalidParamException("type");

    const percentual = Number(technicalRetention.percentual);
    if (!percentual) technicalRetention = markFieldToDelete();

    return ContractRepository.updateContractOnDataBase(uid, {
      technicalRetention,
    });
  }

  private validateTechnicalRetention({
    percentual,
    type,
  }: ITechnicalRetention) {
    if (!type) throw new RequiredParamException("type");

    if (percentual < 0 || percentual > 100) {
      throw new BetweenValuesException(
        percentual,
        0,
        100,
        "technicalRetention"
      );
    }
  }

  /** Atualiza os impostos do contrato no banco de dados. */
  public async updateTaxes(uid: string, taxes: IContractTaxes): Promise<void> {
    if (!uid) throw new RequiredParamException("uid");
    this.validateTaxes(taxes);

    if (Object.keys(taxes).length === 0) {
      taxes = markFieldToDelete();
    }
    return ContractRepository.updateContractOnDataBase(uid, { taxes });
  }

  public validateTaxes(taxes): boolean {
    if (taxes) {
      for (var field in taxes) {
        const value = taxes[field];

        if (value && isNaN(Number(value))) {
          throw new InvalidParamException(
            "Taxes",
            lang("exceptions.contract.invalidTaxes")
          );
        }

        if (value < 0 || value > 100) {
          throw new BetweenValuesException(value, 0, 100, field);
        }

        if (value === 0 || value === undefined) {
          delete taxes[field];
        } else {
          taxes[field] = Number(value);
        }
      }
      return true;
    }

    throw new RequiredParamException("taxes");
  }

  /** Atualiza o contrato adicionando/removendo os usuários compartilhados a partir da lista de IDs.
   * As alterações são salvas no banco de dados.
   */
  public async updateSharedUsers(
    contract: ContractModel,
    usersIds: string[]
  ): Promise<any> {
    if (Object.keys(usersIds).length < 1) {
      throw new Error(lang("exceptions.contract.invalidUserShared"));
    }

    const users = contract.mergeContractUsersWith(usersIds);
    await ContractRepository.updateContractOnDataBase(contract.uid, { users });

    contract.setUsers(users);
  }

  /** Atualiza os dados do orçamento executivo do contrato no banco de dados e
   * retorna se os dados foram removidos ou alterados.
   */
  public async updateExecutiveBudget(
    contract: ContractModel,
    branchId: number,
    budgetId: string
  ): Promise<"removed" | "updated"> {
    let executiveBudget: IExecutiveBudget = { id: budgetId, branchId };
    let type: any = "updated";

    if (!executiveBudget || !executiveBudget.id || !executiveBudget.branchId) {
      executiveBudget = markFieldToDelete();
      type = "removed";
    }

    await ContractRepository.updateContractOnDataBase(contract.uid, {
      executiveBudget,
    });

    contract.setExecutiveBudget(branchId, budgetId);
    return type;
  }

  /** Atualiza o total do contrato no banco de dados e adiciona a
   * flag hasChanged para sinalizar que houve alteração nos valores
   * do mesmo.
   */
  public async updateTotal(
    contract: ContractModel,
    newTotal: number,
    transaction?: Transaction
  ): Promise<void> {
    if (newTotal < 0) newTotal = 0;

    await ContractRepository.updateContractOnDataBase(
      contract.uid,
      { hasChanged: true, total: newTotal },
      transaction
    );

    contract.total = newTotal;
    contract.hasChanged = true;
  }

  public async markContractAsChanged(contract: ContractModel): Promise<void> {
    await ContractRepository.updateContractOnDataBase(contract.uid, {
      hasChanged: true,
    });
    contract.hasChanged = true;
  }

  /** Atualiza os totais medidos e faturados do contrato. */
  public async updateMeasuredTotals(
    contract: ContractModel,
    measuredTotal: number,
    billingTotal: number
  ): Promise<void> {
    if (measuredTotal < 0) measuredTotal = 0;
    if (billingTotal < 0) billingTotal = 0;

    await ContractRepository.updateContractOnDataBase(contract.uid, {
      measuredTotal,
      billingTotal,
    });

    contract.measuredTotal = measuredTotal;
    contract.billingTotal = billingTotal;
  }

  /** Atualiza a lista de ids de anexos. */
  public async updateAttachments(
    contract: ContractModel,
    attachmentsIds: string[],
    transaction?: Transaction
  ): Promise<void> {
    await ContractRepository.updateContractOnDataBase(
      contract.uid,
      { attachments: attachmentsIds },
      transaction
    );

    contract.attachments = attachmentsIds;
  }

  public async upadateCoverImage(contract: ContractModel, imageUrl: string) {
    if (!imageUrl) throw new RequiredParamException("imageUrl");

    await ContractRepository.updateContractOnDataBase(contract.uid, {
      coverImageUrl: imageUrl,
    });
    contract.coverImageUrl = imageUrl;
  }

  /** Obtém do banco as definições de propriedades customizadas do contrato. */
  public getConstractsCustomProperties(contract: ContractModel) {
    return ContractRepository.getCustomProperties(contract.uid);
  }

  public async readjustContract(
    contractId: string,
    currentIndicatorQuotation: IIndicatorQuotation,
    newIndicatorQuotation: IIndicatorQuotation,
    percentualReadjust: number
  ) {
    const data = {
      contractId,
      currentIndicator: currentIndicatorQuotation,
      newIndicator: newIndicatorQuotation,
      finalReadjustmentPercentage: percentualReadjust,
    };

    return axios
      .post(`${process.env.REACT_APP_READJUST_CONTRACT}`, data)
      .catch((error) => {
        console.error(error);
        console.error(
          "Erro no cálculo do Contrato",
          error.response.data?.error
        );
        throw error;
      });
  }

  /** Inscreve o snapshot no contrato selecionado e lança um erro
   * caso o usuário logado não possua permissão de acesso ao mesmo.
   */
  public async subscribeContract(
    uid: string,
    onCreate: (data?: ContractModel) => void,
    onChange: (data?: ContractModel) => void,
    onError?: Function
  ): Promise<Unsubscribe | null> {
    const userId = AuthService.currentUserId;
    return ContractRepository.addContractDocumentSnapshot(
      uid,
      (contract) => {
        if (contract && contract.hasUserId(userId)) {
          onCreate(contract);
        } else {
          console.log("User has no permission to access this contract!");
          onCreate(undefined);
        }
      },
      (contract) => {
        if (contract && contract.hasUserId(userId)) {
          onChange(contract);
        } else {
          console.log("User has no permission to access this contract!");
          onChange(undefined);
        }
      },
      onError
    );
  }

  /** Inscreve snapshot nos contratos que o usuário logado
   * possui acesso.
   */
  public subscribeContractsSnapshot(
    onCreate: (data: ContractModel[]) => void,
    onChange: (data: ContractModel[]) => void,
    onRemove: (data: ContractModel[]) => void
  ) {
    const userId = AuthService.currentUserId;
    return ContractRepository.addContractCollectionSnapshotByUser(
      userId,
      onCreate,
      onChange,
      onRemove
    );
  }
}

export default new ContractService();
